import React, { useContext,  useState } from "react";
import { AuthContext } from "../AuthContext/AuthContext";
import { motion } from "framer-motion";
import {
  User,
  MapPin,
  MessageCircle,
  Phone,
  Mail,
  Edit,
  PenSquare,
  CheckCircle,
  AlertCircle,
  Plus,
  Circle,
} from "lucide-react";
import { Address } from "../../APIs/address";

interface UserInfoCardProps {
  email: string;
  addresses: any[];
  handleOpenModal: (selectedAddress: Address) => void;
  handleOpenInstructionModal: () => void;
  selectedAddress: Address | null;
  setSelectedAddress: (selectedAddress: Address | null) => void;
  updateAddressSelected: (userId: number, addressId: number) => void;
  createLogistics: (selectAdd: Address) => void;
  isCreatingAddress: boolean
  handleisCreatingAddressModal:()=>void;
  handleAddressSelect: (address: Address) => void;
}

const UserInfoCard: React.FC<UserInfoCardProps> = ({
  email,
  addresses,
  handleOpenModal,
  handleOpenInstructionModal,
  setSelectedAddress,
  selectedAddress,
  updateAddressSelected,
  createLogistics,
  isCreatingAddress,
  handleisCreatingAddressModal,
  handleAddressSelect,
}) => {
  const { user } = useContext(AuthContext);
  const [hoveredAddressId, setHoveredAddressId] = useState<number | null>(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 },
  };



  // Show a guide message if no address is selected yet
  const showSelectionGuide = addresses.length > 0 && !selectedAddress;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl overflow-hidden">
      {/* Header */}
      <div className="text-[#005D6E] p-2 md:p-4 bg-white">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <User size={18} />
          Contact & Delivery Details
        </h2>
        <p className="text-[#005D6E] mt-1 text-sm">
          Review and update your information below
        </p>
      </div>

      <div className="p-2 md:p-4">
        <motion.div
          {...fadeInUp}
          className="bg-gray-50 rounded-xl p-2 md:p-4">
          <div className="flex">
            <div className=" p-2 rounded-lg mr-3">
              <User size={20} className="text-[#005D6E]" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg text-[#005D6E] font-semibold mb-2">
                Personal Information
              </h3>
              <div className="space-y-1">
                <div className="text-gray-800 font-medium">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="flex items-center text-gray-600 text-sm">
                  <Mail size={14} className="mr-2 text-gray-400" />
                  {user?.email}
                </div>
                <div className="flex items-center text-gray-600 text-sm">
                  <Phone size={14} className="mr-2 text-gray-400" />
                  {user?.phone_number || "No phone number"}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.1 }}
          className="bg-gray-50 p-2 md:p-4 mt-4 rounded-xl">
          <div className="flex flex-col">
            <div className="flex items-start mb-3">
              <div className=" p-2 rounded-lg mr-3">
                <MapPin size={20} className="text-[#005D6E]" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg text-[#005D6E] font-semibold">
                    Delivery Address
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleisCreatingAddressModal}
                    className="flex items-center gap-1 text-[#75A7AD] text-sm">
                    <Plus size={12} />
                    Add New Address
                  </motion.button>
                </div>
              </div>
            </div>

            {/* No addresses warning */}
            {addresses.length === 0 && (
              <motion.div 
                {...fadeInUp}
                className="p-3 bg-yellow-50 border border-yellow-100 rounded-lg mb-3">
                <div className="flex items-center">
                  <AlertCircle size={16} className="text-[#005D6E] mr-2" />
                  <p className="text-[#005D6E] text-sm font-medium">
                    Step 1: Please add a delivery address to continue
                  </p>
                </div>
              </motion.div>
            )}

            {/* Selection required warning */}
            {showSelectionGuide && (
              <motion.div 
                {...fadeInUp}
                className="p-3 bg-yellow-50 border border-yellow-100 rounded-lg mb-3">
                <div className="flex items-center">
                  <AlertCircle size={16} className="text-[#005D6E] mr-2" />
                  <p className="text-[#005D6E] text-sm font-medium">
                    Step 2: Please select an address for delivery
                  </p>
                </div>
              </motion.div>
            )}

            <div className="space-y-3 max-h-[350px] overflow-y-auto mt-1 w-full">
              {addresses.length > 0 ? (
                addresses.map((address) => (
                  <motion.div
                    key={address.id}
                    whileHover={{ y: -1 }}
                    onMouseEnter={() => setHoveredAddressId(address.id)}
                    onMouseLeave={() => setHoveredAddressId(null)}
                    className={`p-2 md:p-4 bg-white rounded-lg transition-all relative ${
                      selectedAddress?.id === address.id
                        ? "border-2 border-[#005D6E]"
                        : "border border-gray-100 hover:bg-gray-50"
                    }`}>
                    {hoveredAddressId === address.id && selectedAddress?.id !== address.id && (
                      <div className="absolute right-2 top-2 bg-[#005D6E] text-white py-1 px-2 rounded text-xs font-medium z-10">
                        Select this address
                      </div>
                    )}
                    <div className="flex justify-between">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => handleAddressSelect(address)}>
                        <div className="flex justify-between">
                          <p className="text-gray-800 font-medium">
                            {address.address_1}
                          </p>
                          {selectedAddress?.id === address.id ? (
                            <CheckCircle
                              size={18}
                              className="text-[#005D6E] flex-shrink-0"
                            />
                          ) : (
                            <Circle
                              size={18}
                              className="text-gray-300 flex-shrink-0"
                            />
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mt-1">
                          {address.city}, {address.province}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 text-xs font-medium bg-[#75A7AD]/10 text-[#005D6E] rounded-full">
                              {address.address_type}
                            </span>
                            {address.building_type && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                                {address.building_type}
                              </span>
                            )}
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(address);
                            }}
                            className="flex items-center gap-1 text-[#75A7AD] text-xs font-medium">
                            <Edit size={10} />
                            Edit
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-gray-400 italic text-sm">
                    No addresses available. Please add an address.
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          {...fadeInUp}
          transition={{ delay: 0.2 }}
          className={`bg-gray-50 rounded-xl p-4 mt-4 ${!selectedAddress ? 'opacity-50' : ''}`}>
          <div className="flex flex-col">
            <div className="flex items-start mb-3">
              <div className=" p-2 rounded-lg mr-3">
                <MessageCircle size={20} className="text-[#005D6E]" />
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg text-[#005D6E] font-semibold">
                    Special Instructions
                  </h3>
                  {selectedAddress && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleOpenInstructionModal}
                      className="flex items-center gap-1 text-[#75A7AD] text-sm">
                      <PenSquare size={12} />
                      {selectedAddress.special_instruction ? "Update" : "Add"}
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
            {selectedAddress && (
              <motion.div 
                {...fadeInUp}
                className="p-3 bg-blue-50 border border-blue-100 rounded-lg mb-3">
                <div className="flex items-center">
                  <CheckCircle size={16} className="text-[#005D6E] mr-2" />
                  <p className="text-[#005D6E] text-sm">
                    Step 3: {selectedAddress.special_instruction ? "Special instructions added" : "Add special instructions (optional)"}
                  </p>
                </div>
              </motion.div>
            )}
            <div className="p-3 bg-white rounded-lg min-h-12 w-full mt-1">
              {!selectedAddress ? (
                <div className="flex items-center">
                  <AlertCircle size={14} className="text-gray-400 mr-2" />
                  <p className="text-gray-400 italic text-sm">
                    Please select an address first
                  </p>
                </div>
              ) : selectedAddress.special_instruction ? (
                <p className="text-gray-700 text-sm">
                  {selectedAddress.special_instruction}
                </p>
              ) : (
                <p className="text-gray-400 italic text-sm">
                  No special instructions for this address
                </p>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default UserInfoCard;